import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import CountryCodeSelect from "../CountryCodeSelect";

import "../../styles/AddEditVCMAdmin.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function AddEditVCMAdminModal({
  onSuccess,
  editData = null,
  editId = null,
} = {}) {
  const { id: routeId } = useParams();
  const id = editId || routeId;
  const isUpdateMode = Boolean(id);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    country_code: "",
    phone_number: "",
    countryCode: "+94",
    role: "VCM_Admin",
    password: "",
  });

  const [phoneError] = useState("");

  const [passwordRules, setPasswordRules] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
  });

  const [showPassword, setShowPassword] = useState(false);

  const [showRules, setShowRules] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Fetch admin data for update mode
  const fetchVCMAdminData = useCallback(async () => {
    setIsLoadingData(true);
    setErrorMsg("");

    try {
      const url = `${BASE_URL}/api/admin/vcm-auth/get-vcm-admin/${id}`;

      const response = await fetch(url);
      const data = await response.json();

      if (response.ok && data.success) {
        const vcm_admin = data.vcm_admin || data.admin || data.data || data;

        // Use the exact database column names based on backend code
        setFormData({
          name: vcm_admin.name || vcm_admin.Name || "",
          email: vcm_admin.email || vcm_admin.Email || "",
          country_code: vcm_admin.country_code || "",
          phone_number:
            vcm_admin.phone_number || vcm_admin.phone || vcm_admin.Phone || "",
          countryCode: vcm_admin.country_code || "+94",
          role: vcm_admin.role || "VCM_Admin",
          password: "",
        });
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      setErrorMsg("Error fetching admin data. Please try again.");
    } finally {
      setIsLoadingData(false);
    }
  }, [id]);

  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || editData.Name || "",
        email: editData.email || editData.Email || "",
        country_code: editData.country_code || "",
        phone_number:
          editData.phone_number || editData.phone || editData.Phone || "",
        countryCode: editData.country_code || "+94",
        password: "",
      });
      setIsLoadingData(false);
      return;
    }

    if (isUpdateMode && id) {
      fetchVCMAdminData();
    }
  }, [id, isUpdateMode, fetchVCMAdminData, editData]);

  const handleNameChange = (e) => {
    let value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
    setFormData((prev) => ({ ...prev, name: value }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "password") {
      const rules = {
        length: value.length >= 8 && value.length <= 20,
        uppercase: /[A-Z]/.test(value),
        lowercase: /[a-z]/.test(value),
        number: /\d/.test(value),
        specialChar: /[\W_]/.test(value),
      };
      setPasswordRules(rules);
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSuccessMsg("");
    setErrorMsg("");

    // Validate phone number before submitting
    const cleanPhone = formData.phone_number.replace(/\D/g, "");
    if (cleanPhone.length < 7 || cleanPhone.length > 15) {
      setErrorMsg("Phone number must be 7-15 digits");
      setTimeout(() => setErrorMsg(""), 5000);
      setIsLoading(false);
      return;
    }

    // Validate country code
    const cleanCountryCode = formData.countryCode.replace(/\D/g, "");
    if (
      !formData.countryCode.startsWith("+") ||
      cleanCountryCode.length < 1 ||
      cleanCountryCode.length > 4
    ) {
      setErrorMsg("Country code must start with + and be 1-4 digits");
      setTimeout(() => setErrorMsg(""), 5000);
      setIsLoading(false);
      return;
    }

    // Password validation for create mode
    if (!isUpdateMode) {
      const allRulesPassed = Object.values(passwordRules).every((rule) => rule);
      if (!allRulesPassed) {
        setErrorMsg("Please ensure password meets all requirements");
        setTimeout(() => setErrorMsg(""), 5000);
        setIsLoading(false);
        return;
      }
    }

    try {
      if (isUpdateMode) {
        await updateVCMAdmin();
      } else {
        await createVCMAdmin();
      }
    } catch (error) {
      console.error(
        `${isUpdateMode ? "Update" : "Registration"} error:`,
        error
      );
      const errorMessage =
        error.message || "Something went wrong. Please try again.";
      setErrorMsg(errorMessage);

      // Clear error message after 5 seconds
      setTimeout(() => setErrorMsg(""), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const createVCMAdmin = async () => {
    const cleanPhone = formData.phone_number.replace(/\D/g, "");

    const requestBody = {
      name: formData.name,
      email: formData.email,
      country_code: formData.countryCode,
      phone_number: cleanPhone,
      role: formData.role,
      password: formData.password,
      frontend_url: window.location.origin,
    };

    const response = await fetch(
      `${BASE_URL}/api/admin/vcm-auth/create-vcm-admin`,
      {
        headers: { "Content-Type": "application/json" },
        method: "POST",
        body: JSON.stringify(requestBody),
      }
    );

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || "Failed to create VCM admin");
    }

    const message = data.message?.toLowerCase().includes("verification")
      ? `A verification email has been sent to ${formData.email}.`
      : `VCM Admin account created successfully!`;

    setSuccessMsg(message);

    // Reset form
    setFormData({
      name: "",
      email: "",
      phone_number: "",
      countryCode: "+",
      role: "VCM_Admin",
      password: "",
    });

    // Build notification payload for parent
    const notice = {
      type: data.token ? "verification_sent" : "success",
      message,
      token: data.token || null,
      verifyUrl: data.verifyUrl || null,
    };

    if (onSuccess && typeof onSuccess === "function") {
      try {
        onSuccess(notice);
      } catch (e) {
        console.error("onSuccess callback error:", e);
      }
    }

    // Clear success message after 5 seconds
    setTimeout(() => setSuccessMsg(""), 5000);
  };

  const updateVCMAdmin = async () => {
    const cleanPhone = formData.phone_number.replace(/\D/g, "");

    const requestBody = {
      name: formData.name,
      email: formData.email,
      country_code: formData.countryCode,
      phone_number: cleanPhone,
    };

    const response = await fetch(
      `${BASE_URL}/api/admin/vcm-auth/update-vcm-admin/${id}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...requestBody, user_id: id }),
      }
    );

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || "Update failed");
    }

    setSuccessMsg("VCM admin updated successfully!");

    // Clear success message after 5 seconds
    setTimeout(() => setSuccessMsg(""), 5000);

    // Inform parent and close modal after a short delay so user sees success message
    setTimeout(() => {
      if (onSuccess && typeof onSuccess === "function") {
        try {
          onSuccess({
            type: "updated",
            message: "VCM admin updated successfully!",
          });
        } catch (e) {
          console.error("onSuccess callback error:", e);
        }
      }
    }, 1200);
  };

  if (isLoadingData) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {successMsg && (
        <div className="mb-3 text-success text-center fw-semibold">
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <Alert variant="danger" className="text-center">
          {errorMsg}
        </Alert>
      )}

      <Form className="register-form" onSubmit={handleSubmit}>
        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleNameChange}
                placeholder="Enter full name"
                required
              />
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                required
              />
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <div className="d-flex gap-2">
                <CountryCodeSelect
                  value={formData.countryCode}
                  onChange={(code) =>
                    setFormData((prev) => ({ ...prev, countryCode: code }))
                  }
                  placeholder="Code"
                  isRequired={true}
                  width="100px"
                />
                <Form.Control
                  type="text"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={(e) => {
                    // Only allow digits
                    const value = e.target.value.replace(/\D/g, "");
                    setFormData((prev) => ({ ...prev, phone_number: value }));
                  }}
                  placeholder="Enter phone number"
                  required
                  style={{
                    flex: 1,
                    height: "48px",
                    fontSize: "1rem",
                    borderRadius: "12px",
                    backgroundColor: "#f7fafc",
                    border: "2px solid #e2e8f0",
                  }}
                />
              </div>
              <Form.Text className="text-muted">
                Enter phone number with country code (e.g., +94 for Sri Lanka)
              </Form.Text>
              {phoneError && (
                <div className="invalid-feedback d-block">{phoneError}</div>
              )}
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>
                Password
                {isUpdateMode && (
                  <small className="text-muted">
                    {" "}
                    (Leave empty to keep current)
                  </small>
                )}
              </Form.Label>
              <div className="password-wrapper">
                <Form.Control
                  className="password-input"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={
                    isUpdateMode
                      ? "Enter new password (optional)"
                      : "Enter password"
                  }
                  onFocus={() => setShowRules(true)}
                  onBlur={() => setShowRules(false)}
                  required={!isUpdateMode}
                />

                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {showRules && (
                <div className="password-rules mt-2">
                  <small>Password requirements:</small>
                  <ul className="list-unstyled ms-2">
                    <li
                      style={{
                        color: passwordRules.length ? "#28a745" : "#dc3545",
                      }}
                    >
                      {passwordRules.length ? "✅" : "❌"} 8-20 characters
                    </li>
                    <li
                      style={{
                        color: passwordRules.uppercase ? "#28a745" : "#dc3545",
                      }}
                    >
                      {passwordRules.uppercase ? "✅" : "❌"} At least one
                      uppercase letter
                    </li>
                    <li
                      style={{
                        color: passwordRules.lowercase ? "#28a745" : "#dc3545",
                      }}
                    >
                      {passwordRules.lowercase ? "✅" : "❌"} At least one
                      lowercase letter
                    </li>
                    <li
                      style={{
                        color: passwordRules.number ? "#28a745" : "#dc3545",
                      }}
                    >
                      {passwordRules.number ? "✅" : "❌"} At least one number
                    </li>
                    <li
                      style={{
                        color: passwordRules.specialChar
                          ? "#28a745"
                          : "#dc3545",
                      }}
                    >
                      {passwordRules.specialChar ? "✅" : "❌"} At least one
                      special character
                    </li>
                  </ul>
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>

        <div className="text-center">
          <Button type="submit" className="register-btn" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {isUpdateMode ? "Updating..." : "Registering..."}
              </>
            ) : isUpdateMode ? (
              "Update VCM Admin"
            ) : (
              "Register VCM Admin"
            )}
          </Button>
        </div>
      </Form>
    </>
  );
}
